import React, { useState } from 'react';
import { F } from '../theme.jsx';
import { useModalViewport, modalShellStyle, ModalHeader } from '../modal-utils.jsx';
import { useValidation } from '../widgets.jsx';
import { Portal } from '../components/boothly/portal.jsx';
import { Z } from '../lib/z-index.js';
import { useToast } from '../components/boothly/toast-provider.jsx';

// ─────────────────────────────────────────────────────────────
// AccountDeleteSheet — echte Konto-Löschung mit E-Mail-Bestätigung
// ─────────────────────────────────────────────────────────────
export function AccountDeleteSheet({ dark, t, authUser, authToken, onClose, onDeleted }) {
  const toast = useToast();
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { validate, errors, clearError, registerRef } = useValidation({
    emailInput: { required: true, type: "email" },
  });

  const submit = async () => {
    if (submitting) return;
    if (!validate({ emailInput })) return;
    const trimmed = emailInput.trim().toLowerCase();
    if (trimmed !== (authUser?.email || "").toLowerCase()) {
      toast.error("E-Mail stimmt nicht überein");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/me", {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.reason || "Löschung fehlgeschlagen");
        setSubmitting(false);
        return;
      }
      toast.success("Konto gelöscht");
      onDeleted();
    } catch (e) {
      toast.error(e.message || "Netzwerkfehler");
      setSubmitting(false);
    }
  };

  return (
    <Portal>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: Z.nestedBackdrop,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "22px 22px 18px", zIndex: Z.nestedSheet })}>
        <ModalHeader t={t} dark={dark} F={F} title="Konto löschen" onClose={onClose} marginBottom={12} />
        <div style={{ background: t.accent.red.weak, color: t.accent.red.solid, borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12, lineHeight: 1.5, fontWeight: 500 }}>
          Alle Daten werden permanent gelöscht. Diese Aktion ist unwiderruflich.
        </div>
        <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 14 }}>
          Gib zur Bestätigung deine E-Mail-Adresse ein:
        </div>
        <div ref={registerRef("emailInput")} style={{
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          borderRadius: 12, padding: "0 14px", marginBottom: errors.emailInput ? 4 : 14,
          border: `1px solid ${errors.emailInput ? "rgba(255,59,48,0.6)" : t.glassBorder}`,
        }}>
          <input
            type="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
            value={emailInput} onChange={e => { setEmailInput(e.target.value); clearError("emailInput"); }}
            placeholder={authUser?.email || "E-Mail"}
            style={{ width: "100%", boxSizing: "border-box", background: "transparent", border: "none", outline: "none", padding: "13px 0", fontSize: 15, color: t.text, fontFamily: F }}
          />
        </div>
        {errors.emailInput && <div style={{ fontSize: 11, color: "rgba(255,59,48,0.9)", marginBottom: 10, paddingLeft: 4 }}>{errors.emailInput}</div>}
        <button onClick={submit} disabled={submitting} style={{
          width: "100%", padding: "13px",
          background: t.accent.red.solid, color: "#fff",
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: submitting ? "not-allowed" : "pointer",
          marginBottom: 8, opacity: submitting ? 0.7 : 1,
        }}>{submitting ? "Wird gelöscht…" : "Konto endgültig löschen"}</button>
        <button onClick={onClose} style={{
          width: "100%", padding: "10px",
          background: "transparent", border: "none",
          color: t.textMuted,
          fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Abbrechen</button>
      </div>
    </Portal>
  );
}
